<div class="notification-shell">
  <div class="notification-page">
    <header class="page-header">
      <div>
        <p class="page-eyebrow">Broadcast center</p>
        <h1>Notification Management</h1>
        <p>Create and send polished announcements to all users or carefully selected groups.</p>
        <div class="mt-3">
          <ul class="nav nav-tabs">
            <li class="nav-item">
              <a class="nav-link" [class.active]="activeTab === 'send'" href="javascript:void(0)" (click)="setTab('send')">Send Notification</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" [class.active]="activeTab === 'history'" href="javascript:void(0)" (click)="setTab('history')">History</a>
            </li>
          </ul>
        </div>
      </div>
      <button *ngIf="activeTab === 'send'" class="btn btn-primary page-action" type="submit" form="notification-form" [disabled]="!canSubmit">
        <span *ngIf="isSubmitting" class="me-2"><i class="fa fa-spinner fa-spin"></i></span>
        {{ isSubmitting ? 'Sending...' : 'Send Notification' }}
      </button>
    </header>

    <div class="content-grid" *ngIf="activeTab === 'send'">
      <section class="form-card">
        <div class="card-header">
          <div>
            <p class="section-label">Compose</p>
            <h2>New announcement</h2>
          </div>
          <span class="status-pill" [class.status-pill--saved]="draftSaved">{{ draftSaved ? 'Draft saved' : 'Unsaved' }}</span>
        </div>

        <form id="notification-form" [formGroup]="notificationForm" (ngSubmit)="onSubmit()" novalidate>
          <div class="field-grid">
            <div class="field-group">
              <label class="field-label" for="targetType">Recipients</label>
              <select id="targetType" class="form-control" formControlName="targetType">
                <option value="all">All Users</option>
                <option value="users">Premium Users</option>
                <option value="single">Specific User</option>
                <option value="tokens">Consultants</option>
              </select>
              <div class="field-hint">Choose who should receive the announcement.</div>
              <div class="error-text" *ngIf="hasError('targetType', 'required')">Recipient selection is required.</div>
            </div>

            <div class="field-group field-group--full">
              <label class="field-label" for="channels">Delivery channel</label>
              <div class="chip-group" role="radiogroup" aria-label="Delivery channel">
                <label class="chip-option" *ngFor="let option of channelOptions">
                  <input type="radio" [value]="option.value" formControlName="channels" />
                  <span>{{ option.label }}</span>
                </label>
              </div>
              <div class="field-hint">Choose either push notifications or email.</div>
              <div class="error-text" *ngIf="hasError('channels', 'required')">A delivery channel is required.</div>
            </div>

            <div class="field-group field-group--full">
              <label class="field-label" for="title">Title</label>
              <input id="title" class="form-control" formControlName="title" maxlength="80" placeholder="Add a short, clear title" />
              <div class="field-meta">
                <span class="field-hint">Keep it concise and actionable.</span>
                <span class="field-counter">{{ titleLength }}/80</span>
              </div>
              <div class="error-text" *ngIf="hasError('title', 'required')">Title is required.</div>
              <div class="error-text" *ngIf="hasError('title', 'maxlength')">Title should stay under 80 characters.</div>
            </div>

            <div class="field-group field-group--full">
              <label class="field-label" for="body">Message</label>
              <textarea id="body" class="form-control textarea-control" rows="5" formControlName="body" maxlength="500" placeholder="Write the message that should appear in the notification"></textarea>
              <div class="field-meta">
                <span class="field-hint">Make the message clear, friendly, and useful.</span>
                <span class="field-counter">{{ bodyLength }}/500</span>
              </div>
              <div class="error-text" *ngIf="hasError('body', 'required')">Message is required.</div>
              <div class="error-text" *ngIf="hasError('body', 'maxlength')">Message should stay under 500 characters.</div>
            </div>

            <div class="field-group field-group--full">
              <label class="field-label" for="includeInactiveUsers">User scope</label>
              <div class="toggle-stack">
                <label class="toggle-row">
                  <input type="checkbox" formControlName="includeInactiveUsers" />
                  <span>Include inactive users</span>
                </label>
                <label class="toggle-row">
                  <input type="checkbox" formControlName="onlyInactiveUsers" />
                  <span>Send only to inactive users</span>
                </label>
              </div>
              <div class="field-hint">Leave both off to target active users only.</div>
            </div>

            <div class="field-group field-group--full" *ngIf="selectedChannel === 'email'">
              <label class="field-label" for="emailSubject">Email subject</label>
              <input id="emailSubject" class="form-control" formControlName="emailSubject" maxlength="120" placeholder="Optional subject for email delivery" />
              <div class="field-hint">Use the shared title and message as defaults when this is left blank.</div>
            </div>

            <div class="field-group field-group--full" *ngIf="selectedChannel === 'email'">
              <label class="field-label" for="emailHtml">Email HTML</label>
              <textarea id="emailHtml" class="form-control textarea-control" rows="4" formControlName="emailHtml" placeholder="Optional HTML body for email delivery"></textarea>
            </div>

            <div class="field-group field-group--full">
              <button class="toggle-link" type="button" (click)="toggleOptionalData()">
                <i class="fa" [ngClass]="showOptionalData ? 'fa-minus' : 'fa-plus'"></i>
                {{ showOptionalData ? 'Hide' : 'Show' }} optional JSON payload
              </button>
              <div *ngIf="showOptionalData" class="json-panel">
                <textarea id="data" class="form-control textarea-control" rows="3" formControlName="data" placeholder='{"type":"offer","id":1}'></textarea>
              </div>
            </div>

            <div class="field-group field-group--full" *ngIf="selectedTarget === 'single'">
              <label class="field-label" for="recipientEmail">Recipient</label>
              <input id="recipientEmail" class="form-control" formControlName="recipientEmail" placeholder="Search by email" />
              <div class="recipient-list" *ngIf="selectedTarget === 'single'">
                <div class="recipient-list__item" *ngFor="let option of recipientOptions" (click)="notificationForm.get('recipientEmail')?.setValue(option.value)">
                  {{ option.label }}
                </div>
                <div class="recipient-list__loading" *ngIf="isLoadingRecipients">Loading recipients...</div>
                <div class="recipient-list__more" *ngIf="recipientHasMore && !isLoadingRecipients" (click)="loadMoreRecipients()">Load more</div>
              </div>
              <div class="field-hint" *ngIf="isLoadingRecipients">Loading recipients...</div>
              <div class="error-text" *ngIf="hasError('recipientEmail', 'required')">Please select a recipient from the list.</div>
            </div>

            <div class="field-group field-group--full" *ngIf="selectedTarget === 'users'">
              <label class="field-label" for="userIds">User IDs</label>
              <textarea id="userIds" class="form-control textarea-control" rows="2" formControlName="userIds" placeholder="Comma separated user ids"></textarea>
              <div class="error-text" *ngIf="hasError('userIds', 'required')">At least one user ID is required.</div>
            </div>

            <div class="field-group field-group--full" *ngIf="selectedTarget === 'tokens'">
              <label class="field-label" for="fcmTokens">FCM tokens</label>
              <textarea id="fcmTokens" class="form-control textarea-control" rows="2" formControlName="fcmTokens" placeholder="Comma separated FCM tokens"></textarea>
              <div class="error-text" *ngIf="hasError('fcmTokens', 'required')">At least one token is required.</div>
            </div>
          </div>

          <div class="action-bar">
            <button class="btn btn-secondary" type="button" (click)="resetForm()">Cancel</button>
            <button class="btn btn-outline" type="button" (click)="saveDraft()">Save Draft</button>
            <button class="btn btn-outline" type="button" (click)="previewAction()">Preview</button>
            <button class="btn btn-primary" type="submit" [disabled]="!canSubmit">
              <span *ngIf="isSubmitting" class="me-2"><i class="fa fa-spinner fa-spin"></i></span>
              {{ isSubmitting ? 'Sending...' : 'Send Notification' }}
            </button>
          </div>
        </form>
      </section>

      <aside class="preview-column">
        <div class="preview-card" [class.preview-card--focused]="previewFocused">
          <div class="card-header card-header--small">
            <div>
              <p class="section-label">Live preview</p>
              <h2>Mobile notification</h2>
            </div>
            <span class="status-pill status-pill--soft">Updating</span>
          </div>

          <div class="phone-shell" aria-live="polite">
            <div class="phone-notch"></div>
            <div class="phone-screen">
              <div class="preview-header">
                <div class="preview-icon">
                  <i class="fa fa-bell"></i>
                </div>
                <div>
                  <div class="preview-app">Amaly</div>
                  <div class="preview-time">Just now</div>
                </div>
              </div>
              <div class="preview-badge">{{ previewBadge }}</div>
              <h3>{{ previewTitle }}</h3>
              <p>{{ previewBody }}</p>
            </div>
          </div>

          <div class="summary-card">
            <div class="summary-row">
              <span>Recipients</span>
              <strong>{{ recipientSummary }}</strong>
            </div>
            <div class="summary-row">
              <span>Estimated reach</span>
              <strong>3,428 users</strong>
            </div>
            <div class="summary-row">
              <span>Delivery</span>
              <strong>{{ selectedChannel === 'email' ? 'Email delivery' : 'Push delivery' }}</strong>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <div class="history-view mt-4" *ngIf="activeTab === 'history'">
      <div class="card">
        <div class="card-body">
          <div *ngIf="historyLoading" class="text-center py-4">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading history...</p>
          </div>
          
          <div *ngIf="!historyLoading && (!historyData || historyData.length === 0)" class="text-center py-4 text-muted">
            No notification history found.
          </div>

          <div class="table-responsive" *ngIf="!historyLoading && historyData && historyData.length > 0">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Target</th>
                  <th>Channels</th>
                  <th>Status</th>
                  <th>Push (Success/Fail)</th>
                  <th>Email (Success/Fail)</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of historyData">
                  <td>{{ item.createdAt | date:'short' }}</td>
                  <td><strong>{{ item.title }}</strong><br><small class="text-muted">{{ item.body | slice:0:50 }}{{ item.body?.length > 50 ? '...' : '' }}</small></td>
                  <td>{{ item.target }}</td>
                  <td>{{ item.channels?.join(', ') }}</td>
                  <td>
                    <span class="badge" 
                          [class.bg-warning]="item.status === 'Processing'" 
                          [class.bg-success]="item.status === 'Completed'" 
                          [class.bg-danger]="item.status === 'Failed'">
                      {{ item.status }}
                    </span>
                  </td>
                  <td>{{ item.totals?.pushSuccess || 0 }} / {{ item.totals?.pushFailed || 0 }}</td>
                  <td>{{ item.totals?.emailSuccess || 0 }} / {{ item.totals?.emailFailed || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="d-flex justify-content-between align-items-center mt-3" *ngIf="!historyLoading && historyTotal > historyLimit">
            <span>Showing {{ (historyPage - 1) * historyLimit + 1 }} - {{ historyPage * historyLimit < historyTotal ? historyPage * historyLimit : historyTotal }} of {{ historyTotal }}</span>
            <div>
              <button class="btn btn-sm btn-outline-secondary me-2" [disabled]="historyPage === 1" (click)="changeHistoryPage(historyPage - 1)">Previous</button>
              <button class="btn btn-sm btn-outline-secondary" [disabled]="historyPage * historyLimit >= historyTotal" (click)="changeHistoryPage(historyPage + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="response-card" *ngIf="responseSummary && activeTab === 'send'">
      <strong>Broadcast summary</strong>
      <div class="response-grid">
        <div><span>Total</span><strong>{{ responseSummary.total }}</strong></div>
        <div><span>Successful</span><strong>{{ responseSummary.success }}</strong></div>
        <div><span>Failed</span><strong>{{ responseSummary.failed }}</strong></div>
      </div>
    </div>
  </div>
</div>
